
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Employee, EmployeeFormData, EmployeeFiles } from "@/types/employee";
import { useAssignRoleToEmployee, useRemoveRoleFromEmployee } from "./useRoles";
import { AppRole } from "./useUserRoles";

export const useEmployees = () => {
  const queryClient = useQueryClient();
  const assignRoleMutation = useAssignRoleToEmployee();
  const removeRoleMutation = useRemoveRoleFromEmployee();

  const { data: employees = [], isLoading: loading, error } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("*, employee_departments(departments(id, name))")
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      const employeesWithDepartments = data.map(emp => {
          const departments = emp.employee_departments.map((ed: any) => ed.departments).filter(Boolean);
          // @ts-ignore
          delete emp.employee_departments;
          return { ...emp, departments };
      });
      return employeesWithDepartments as Employee[];
    },
  });

  const saveEmployeeMutation = useMutation({
    mutationFn: async ({ 
      formData, 
      files, 
      editingEmployee, 
      roleIds, 
      departmentIds,
      userRoles 
    }: { 
      formData: EmployeeFormData; 
      files: EmployeeFiles; 
      editingEmployee: Employee | null;
      roleIds: string[];
      departmentIds: string[];
      userRoles?: AppRole[];
    }) => {
      const { department, ...restOfFormData } = formData;
      const employeeData = {
        ...restOfFormData,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        electronic_accounts: formData.electronic_accounts ? JSON.parse(formData.electronic_accounts) : null,
        department: department || 'غير محدد', // Set a default for the old column
        // تصحيح التواريخ الفارغة
        birth_date: formData.birth_date || null,
        hiring_date: formData.hiring_date || null,
      };

      let savedEmployee: any;

      if (editingEmployee?.id) {
        // Update existing employee
        const { data, error } = await supabase
          .from("employees")
          .update(employeeData)
          .eq("id", editingEmployee.id)
          .select()
          .single();
        
        if (error) throw error;
        savedEmployee = data;
      } else {
        // Create new employee
        const { data, error } = await supabase
          .from("employees")
          .insert([employeeData])
          .select()
          .single();
        
        if (error) throw error;
        savedEmployee = data;
      }

      // Handle role assignments (custom_roles - النظام القديم)
      if (savedEmployee) {
        const { data: currentRoles } = await supabase
          .from('employee_roles')
          .select('role_id')
          .eq('employee_id', savedEmployee.id);
        
        const currentRoleIds = currentRoles?.map(r => r.role_id) || [];

        const rolesToAdd = roleIds.filter(id => !currentRoleIds.includes(id));
        const rolesToRemove = currentRoleIds.filter(id => !roleIds.includes(id));

        await Promise.all(rolesToAdd.map(roleId => 
          assignRoleMutation.mutateAsync({ employeeId: savedEmployee.id, roleId })
        ));

        await Promise.all(rolesToRemove.map(roleId => 
          removeRoleMutation.mutateAsync({ employeeId: savedEmployee.id, roleId })
        ));
      }
      
      // Handle user_roles assignments (النظام الجديد - RBAC)
      if (savedEmployee && savedEmployee.user_id && userRoles && userRoles.length > 0) {
        // TODO: سيتم تفعيل هذا بعد تطبيق Migration
        console.log('User roles to be assigned after migration:', {
          userId: savedEmployee.user_id,
          roles: userRoles
        });
        
        // تخزين الأدوار في metadata للموظف حالياً كحل مؤقت
        try {
          await supabase
            .from('employees')
            .update({
              // استخدام حقل موجود لتخزين البيانات مؤقتاً
              electronic_accounts: {
                ...savedEmployee.electronic_accounts,
                pending_user_roles: userRoles
              }
            })
            .eq('id', savedEmployee.id);
        } catch (error) {
          console.error('Error storing pending roles:', error);
        }
      }
      
      // Handle department assignments
      if (savedEmployee) {
        const { data: currentDepartments } = await supabase
          .from('employee_departments')
          .select('department_id')
          .eq('employee_id', savedEmployee.id);
        
        const currentDepartmentIds = currentDepartments?.map(d => d.department_id) || [];

        const departmentsToAdd = departmentIds.filter(id => !currentDepartmentIds.includes(id));
        const departmentsToRemove = currentDepartmentIds.filter(id => !departmentIds.includes(id));

        if (departmentsToAdd.length > 0) {
            await supabase.from('employee_departments').insert(departmentsToAdd.map(department_id => ({ employee_id: savedEmployee.id, department_id })));
        }
        if (departmentsToRemove.length > 0) {
            await supabase.from('employee_departments').delete().in('department_id', departmentsToRemove).eq('employee_id', savedEmployee.id);
        }
      }

      return savedEmployee;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      if (data) {
        queryClient.invalidateQueries({ queryKey: ["employee-roles", data.id] });
      }
    },
  });

  const deleteEmployeeMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const { error } = await supabase
        .from("employees")
        .delete()
        .eq("id", employeeId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
    },
  });

  const saveEmployee = async (
    formData: EmployeeFormData, 
    files: EmployeeFiles, 
    editingEmployee: Employee | null, 
    roleIds: string[], 
    departmentIds: string[],
    userRoles?: AppRole[]
  ): Promise<boolean> => {
    try {
      await saveEmployeeMutation.mutateAsync({ 
        formData, 
        files, 
        editingEmployee, 
        roleIds, 
        departmentIds,
        userRoles 
      });
      return true;
    } catch (error) {
      console.error("Error saving employee:", error);
      return false;
    }
  };

  const deleteEmployee = (employee: Employee) => {
    deleteEmployeeMutation.mutate(employee.id);
  };

  return {
    employees,
    loading,
    uploading: saveEmployeeMutation.isPending,
    saveEmployee,
    deleteEmployee,
    error
  };
};
